import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Typography, Select, Input, Empty, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import DraggableTemplate from './DraggableTemplate';
import { listRecommendationCategories } from '../../services/api/odinApi';

const { Text } = Typography;

const PaletteContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl || '12px'};
  padding: ${({ theme }) => theme.spacing.md || '1rem'};
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(100vh - 200px);
`;

const PaletteTitle = styled(Text)`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent} !important;
  margin-bottom: ${({ theme }) => theme.spacing.sm || '0.5rem'};
  display: block;
`;

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs || '0.25rem'};
  margin-bottom: ${({ theme }) => theme.spacing.md || '1rem'};
`;

const TemplateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs || '0.25rem'};
  overflow-y: auto;
  flex: 1;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--odin-border-strong);
    border-radius: 2px;
  }
`;

const CountLabel = styled(Text)`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted} !important;
  margin-bottom: ${({ theme }) => theme.spacing.xs || '0.25rem'};
  display: block;
`;

const TemplatePalette = ({ templates, loading }) => {
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await listRecommendationCategories();
        if (response?.categories) {
          setCategories([
            { value: '', label: 'All Categories', icon: null, count: null },
            ...response.categories.map(cat => ({
              value: cat.category,
              label: cat.category,
              icon: cat.icon,
              count: cat.count
            }))
          ]);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };

    fetchCategories();
  }, []);

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    return templates.filter((t) => {
      // Filter by category instead of platform
      if (categoryFilter && t.category !== categoryFilter) return false;

      if (searchText) {
        const search = searchText.toLowerCase();
        return (
          t.service.toLowerCase().includes(search) ||
          (t.description && t.description.toLowerCase().includes(search)) ||
          (t.category && t.category.toLowerCase().includes(search))
        );
      }
      return true;
    });
  }, [templates, categoryFilter, searchText]);

  return (
    <PaletteContainer>
      <PaletteTitle>Templates</PaletteTitle>

      <Filters>
        <Select
          value={categoryFilter}
          onChange={setCategoryFilter}
          placeholder="Filter by category"
          size="small"
          style={{ width: '100%' }}
          allowClear
        >
          {categories.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.icon && <span style={{ marginRight: 8 }}>{opt.icon}</span>}
              {opt.label}
              {opt.count && <span style={{ marginLeft: 8, color: 'var(--odin-text-tertiary)' }}>({opt.count})</span>}
            </Select.Option>
          ))}
        </Select>
        <Input
          placeholder="Search..."
          prefix={<SearchOutlined />}
          size="small"
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Filters>

      <CountLabel>
        {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
      </CountLabel>

      <TemplateList>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <Spin size="small" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <Empty
            description="No templates"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '1rem 0' }}
          />
        ) : (
          filteredTemplates.map((template) => (
            <DraggableTemplate key={template.id} template={template} />
          ))
        )}
      </TemplateList>
    </PaletteContainer>
  );
};

export default React.memo(TemplatePalette);
